import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { authClient } from "@/lib/auth-client";
import { useNavigate } from "react-router";
import { Loader2, Check } from "lucide-react";
import { useRightPanel } from "@/hooks/useRightPanel";
import FormShell from "@/components/form-shell";

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, "Le mot de passe doit contenir au moins 8 caractères"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  });

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordPage() {
  const { setRight } = useRightPanel();
  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { password: "", confirmPassword: "" },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token");

  // Set right panel content
  useEffect(() => {
    setRight(<div className="flex h-full flex-1 items-center justify-center overflow-hidden p-6 text-6xl font-bold text-white">Nautika</div>);
  }, [setRight]);

  // Redirige si error=INVALID_TOKEN dans l'URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("error") === "INVALID_TOKEN") {
      navigate("/login?error=INVALID_TOKEN_RESET_PASSWORD", { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (data: ResetPasswordFormValues) => {
    if (!token) {
      toast.error("Lien invalide ou expiré");
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await authClient.resetPassword({
        newPassword: data.password,
        token,
      });
      if (error) {
        toast.error("Erreur lors de la réinitialisation du mot de passe", { description: error.message });
      } else {
        setPasswordReset(true);
      }
    } catch {
      toast.error("Une erreur est survenue");
    }
    setIsLoading(false);
  };

  return (
    <FormShell
      title={passwordReset ? "Mot de passe mis à jour" : "Nouveau mot de passe"}
      subtitle={
        passwordReset
          ? "Votre mot de passe a été mis à jour avec succès. Vous pouvez maintenant vous connecter."
          : "Choisissez un nouveau mot de passe sécurisé pour votre compte."
      }
      onSubmit={passwordReset ? undefined : form.handleSubmit(onSubmit)}
    >
      <Form {...form}>
        <div className="flex flex-col gap-6">
          {passwordReset ? (
            <div className="flex flex-col items-center gap-4">
              <Check className="mx-auto h-12 w-12 text-green-500" />
            </div>
          ) : (
            <>
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nouveau mot de passe *</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmer le nouveau mot de passe *</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          <Button
            type={passwordReset ? "button" : "submit"}
            className="w-full"
            disabled={isLoading}
            onClick={passwordReset ? () => navigate("/login", { replace: true }) : undefined}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Mise à jour...
              </>
            ) : passwordReset ? (
              "Retour à la connexion"
            ) : (
              "Mettre à jour le mot de passe"
            )}
          </Button>
        </div>
      </Form>
    </FormShell>
  );
}
